import { Card, CardHeader } from '../../../components/ui/Card';
import { InfoTip } from '../../../components/ui/InfoTip';
import { CHART_SERIES } from '../../../components/ui/chart';
import { Table, THead, Th, Td, Tr, TableFooter, TTotalRow } from '../../../components/ui/Table';
import { EmptyState, LoadingState } from '../../../components/ui/States';
import type { GtmMqlReport } from '../../../api/gtm';
import { fmtCurrency, fmtNumber, fmtPercent } from './components/format';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "2026-09" as "Sep 2026" (fixed names: en-GB prints "Sept"). */
function monthLabel(month: string): string {
  return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}

/** One decimal only when the target is not a whole number (a part month). */
const fmtTarget = (n: number) => (Number.isInteger(n) ? fmtNumber(n) : n.toFixed(1));

/**
 * MQLs against the monthly target. An MQL is decided by what the person asked
 * for (demo booking, trial request, webinar registration, pricing request), not
 * by a deal opening afterwards: that is the funnel's "Lead to deal opened".
 */
export function MqlCard({ report, loading }: { report: GtmMqlReport | null | undefined; loading: boolean }) {
  return (
    <Card>
      <CardHeader
        title="MQLs vs target"
        description={
          report
            ? `${report.targetLabel} target: ${fmtNumber(report.monthlyTarget)} qualified MQLs a month. Raw = every non-test submission; qualified = passes the rule`
            : 'Qualified MQLs against the monthly target'
        }
        action={
          report && (
            <InfoTip label="How an MQL is counted">
              <div className="space-y-1.5">
                <p>
                  MQL types: demo bookings (website demo forms, and Calendly bookings that started on the website or on a tagged
                  link, not canceled ones), trial requests, webinar registrations and pricing requests. One per person per type per month.
                  The “Demos booked” tile on the Overview does count a demo that was canceled later, so it can sit above the demo MQLs.
                </p>
                <p className="font-medium text-text-primary">Qualified means:</p>
                <ul className="list-disc space-y-0.5 pl-4">
                  {report.qualificationRule.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {!report.competitorCheck && (
                  <p>The competitor check did nothing in this period: the brand registry has no competitor domains.</p>
                )}
                <p>
                  Not the funnel’s “Lead to deal opened” step, which asks whether the lead’s company opened a deal. That one was
                  labelled MQL until 15 Oct. It is lead to deal, so the small number shown there was never an MQL count against this target.
                </p>
              </div>
            </InfoTip>
          )
        }
      />
      {loading ? (
        <LoadingState />
      ) : !report ? (
        <EmptyState compact title="MQL data unavailable for this period." />
      ) : (
        <MqlBody report={report} />
      )}
    </Card>
  );
}

function MqlBody({ report }: { report: GtmMqlReport }) {
  const pct = report.target > 0 ? (report.qualifiedTotal / report.target) * 100 : null;
  const barWidth = pct === null ? 0 : Math.min(pct, 100);
  const multiMonth = report.months.length > 1;
  const partMonth = report.months.some((m) => m.days < m.daysInMonth);
  const o = report.other;
  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[2rem] font-light leading-none tracking-[-0.03em] tabular-nums text-text-primary">
            {fmtNumber(report.qualifiedTotal)}
          </span>
          <span className="text-body-sm text-text-secondary">
            qualified of a {fmtTarget(report.target)} target{pct !== null && ` (${fmtPercent(pct, 0)})`}
          </span>
        </div>
        <span className="relative mt-3 block h-2.5 rounded-full bg-surface-tertiary" role="img" aria-label={`${fmtNumber(report.qualifiedTotal)} of ${fmtTarget(report.target)}`}>
          <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${barWidth}%`, background: CHART_SERIES[0] }} />
        </span>
        {partMonth && (
          <p className="mt-2 text-body-xs text-text-tertiary">
            The {fmtNumber(report.monthlyTarget)} a month is prorated by days for a period that is not a whole month.
          </p>
        )}
      </div>

      <Table>
        <THead>
          <tr>
            <Th>MQL type</Th>
            <Th align="right">Raw</Th>
            <Th align="right">Qualified</Th>
          </tr>
        </THead>
        <tbody>
          {report.byType.map((t) => (
            <Tr key={t.type}>
              <Td className="text-text-primary">{t.label}</Td>
              <Td align="right">{fmtNumber(t.raw)}</Td>
              <Td align="right">{fmtNumber(t.qualified)}</Td>
            </Tr>
          ))}
          <TTotalRow>
            <Td>Total</Td>
            <Td align="right">{fmtNumber(report.rawTotal)}</Td>
            <Td align="right">{fmtNumber(report.qualifiedTotal)}</Td>
          </TTotalRow>
        </tbody>
      </Table>

      {multiMonth && (
        <Table>
          <THead>
            <tr>
              <Th>Month</Th>
              <Th align="right">Raw</Th>
              <Th align="right">Qualified</Th>
              <Th align="right">Target</Th>
            </tr>
          </THead>
          <tbody>
            {report.months.map((m) => (
              <Tr key={m.month}>
                <Td className="text-text-primary">
                  {monthLabel(m.month)}
                  {m.days < m.daysInMonth && <span className="text-text-tertiary"> ({m.days} of {m.daysInMonth} days)</span>}
                </Td>
                <Td align="right">{fmtNumber(m.raw)}</Td>
                <Td align="right">{fmtNumber(m.qualified)}</Td>
                <Td align="right">{fmtTarget(m.target)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <div>
        <p className="eyebrow">Other leads, not MQLs</p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <span className="text-body-sm text-text-primary">
            <span className="font-medium tabular-nums">{fmtNumber(o.leads)}</span> LinkedIn lead gen form leads (content downloads)
          </span>
          <span className="text-body-sm text-text-secondary">
            {o.costPerLead !== null && o.spend !== null
              ? `${fmtCurrency(o.costPerLead)} per lead (${fmtCurrency(o.spend)} spend)`
              : 'No cost per lead: no LinkedIn spend or no leads'}
            {/* The basis sits beside the value: a CPL on all LinkedIn spend reads very differently from one on matched campaigns. */}
            {o.spendNote && <span className="text-body-xs text-text-tertiary"> · {o.spendNote}</span>}
          </span>
        </div>
      </div>

      <TableFooter>
        <span>
          {report.disqualified.length > 0
            ? `${fmtNumber(report.rawTotal - report.qualifiedTotal)} raw MQLs did not qualify: ${report.disqualified.map((d) => `${d.label} ${fmtNumber(d.count)}`).join(', ')}.`
            : 'Every raw MQL qualified.'}{' '}
          {report.calendlyOnlyDemos > 0 &&
            `${fmtNumber(report.calendlyOnlyDemos)} demo ${report.calendlyOnlyDemos === 1 ? 'booking has' : 'bookings have'} no demo form behind ${report.calendlyOnlyDemos === 1 ? 'it' : 'them'} and count on their own.`}
        </span>
      </TableFooter>
    </div>
  );
}
